'use client';

import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { Toast } from '@/components/Toast';

export type ToastData = {
  id: string;
  title: string;
  description: string | ReactNode;
  onDismiss?: () => void;
  className?: string;
  isVisible?: boolean;
  variant?: 'primary';
};

interface ToastContextType {
  pushToast: (toast: Omit<ToastData, 'id'>) => void;
  removeToast: (id: string) => void;
  toasts: ToastData[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = new Date().getTime().toString();
    const newToast = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);

    // if (toast.duration !== 0) {
    //   setTimeout(() => {
    //     removeToast(id);
    //   }, toast.duration || 5000);
    // }
  }, []);

  return (
    <ToastContext.Provider value={{ pushToast, removeToast, toasts }}>
      {children}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto transition-all duration-300 ease-in-out"
          >
            <Toast
              title={toast.title}
              description={toast.description}
              isVisible={toast?.isVisible ?? true}
              variant="primary"
              onDismiss={() => {
                removeToast(toast.id);
                if (toast.onDismiss) toast.onDismiss();
              }}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
